import { useMemo } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { FetchingDot, SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import { LEVEL_COLORS } from "../../palette";
import type { MetricSeries } from "../../types";
import { QueryBody, StatTile } from "../requests/bits";
import { coveredMinutes } from "../requests/utils";
import { EVENT_LEVELS } from "./utils";

/** Events of the board window per level: totals (every occurrence, folded repeats included) and a chart. */
export function EventsOverview({ metrics }: { metrics: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  return (
    <SectionCard
      title={t("monitoring.events.overview.title", { window: t(`monitoring.window.long.${window}`) })}
      description={t("monitoring.events.overview.description")}
      actions={<FetchingDot active={metrics.isFetching} />}
    >
      <QueryBody query={metrics} height={260}>
        {(data) => <OverviewBody data={data} fetchedAt={metrics.dataUpdatedAt} />}
      </QueryBody>
    </SectionCard>
  );
}

function OverviewBody({ data, fetchedAt }: { data: MetricSeries; fetchedAt: number }) {
  const { t, formatNumber } = useLocalization();
  const hours = coveredMinutes(data, fetchedAt) / 60;
  const series = useMemo<ChartSeries[]>(
    () =>
      EVENT_LEVELS.map((level) => ({
        key: level,
        label: t(`monitoring.level.${level}`),
        color: LEVEL_COLORS[level],
        values: data.counters[`events.${level}`] ?? [],
        type: "bar",
        stackId: "events",
      })),
    [data, t],
  );
  const total = (level: string) => data.totals.counters[`events.${level}`] ?? 0;
  const perHour = (count: number) =>
    t("monitoring.events.overview.perHour", {
      rate: formatNumber(count / hours, { maximumFractionDigits: count / hours < 10 ? 1 : 0 }),
    });
  const errors = total("error");
  const warnings = total("warning");
  const infos = total("info");

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatTile
          label={t("monitoring.events.overview.errors")}
          value={formatCount(errors)}
          tone={errors > 0 ? "nok" : "neutral"}
          detail={perHour(errors)}
        />
        <StatTile
          label={t("monitoring.events.overview.warnings")}
          value={formatCount(warnings)}
          tone={warnings > 0 ? "warn" : "neutral"}
          detail={perHour(warnings)}
        />
        <StatTile label={t("monitoring.events.overview.infos")} value={formatCount(infos)} detail={perHour(infos)} />
      </div>
      <TimeSeriesChart
        t={data.t}
        stepMs={data.step_ms}
        window={data.window}
        series={series}
        height={200}
        integer
        format={(v) => formatCount(v)}
        emptyText={t("monitoring.events.overview.empty")}
      />
    </div>
  );
}
